import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Terminal, ShieldCheck, Headphones, FileText, CheckCircle } from "lucide-react";
import { PartnerBenefits, TrustedProven, PartnerCTA } from "@/components/PartnerSharedBlocks";
import SEOHead from "@/components/SEOHead";
import { fadeUp, stagger, viewportOnce, viewportOnceSmall } from "@/utils/motionVariants";

const BUILD_ITEMS = [
  "Fully customised checkout experiences, branded end-to-end",
  "SaaS and subscription platforms with Stored Shopper tokenisation",
  "Marketplaces and multi-merchant splits",
  "Mobile apps with in-app payments",
  "MOTO and Pay by Link flows for phone-based sales",
  "Multi-currency conversion across 100+ currencies",
  "Recurring billing, metered billing and usage-based billing",
  "Embedded finance and merchant cash advance integrations",
];

function ApiHero() {
  return (
    <section className="hero-gradient py-16 md:py-24" data-testid="api-hero">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl">
          <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Developer API</motion.p>
          <motion.h1 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-6">
            Build your own <span className="text-brand-aqua">payment integration</span>
          </motion.h1>
          <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed mb-8">
            Full API access. Full sandbox. Full support. CanDo Payments gives you the building blocks to create bespoke checkout experiences, SaaS platforms and marketplaces — backed by the same infrastructure that powers some of the world's biggest brands.
          </motion.p>
          <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
            <Link to="/contact" className="bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-colors" data-testid="api-cta-sandbox">Get Sandbox Access <ArrowRight size={16} /></Link>
            <a href="/book" className="bg-white text-brand-black hover:bg-gray-50 border border-brand-light-grey font-semibold rounded-full px-8 py-3.5 text-sm transition-colors" data-testid="api-cta-engineer">Talk to a Solution Engineer</a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function WhatYouCanBuild() {
  return (
    <section className="py-20 bg-white" data-testid="api-build">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">What You Can Build</p>
            <h2 className="font-heading text-3xl font-bold text-brand-black tracking-tight mb-4">Our API is the same one that powers our official plugins</h2>
            <p className="text-brand-grey leading-relaxed">So you're never working on a stripped-down version. Build exactly what your project needs, on your stack, on your timeline.</p>
          </div>
          <div className="space-y-3">
            {BUILD_ITEMS.map((item) => (
              <div key={item} className="flex items-start gap-2 text-sm text-brand-black">
                <CheckCircle size={16} className="text-brand-aqua flex-shrink-0 mt-0.5" />{item}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ApiGetStarted() {
  return (
    <section className="py-20 bg-gray-50" data-testid="api-started">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-heading text-3xl font-bold text-brand-black tracking-tight text-center mb-10">How to get started</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { num: "01", title: "Request sandbox", desc: "Fill out a short form and we'll send your sandbox credentials within one working day. No sales call needed to get building." },
            { num: "02", title: "Build and test", desc: "Integrate against the sandbox, test against every scenario you need, and get an engineer on the line whenever you get stuck." },
            { num: "03", title: "Go live", desc: "Once your client is onboarded, we flip the switch to production. Same API, same codebase, zero surprises." },
          ].map((s) => (
            <div key={s.num} className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-brand-aqua text-white font-heading text-2xl font-bold flex items-center justify-center mx-auto mb-5">{s.num}</div>
              <h3 className="font-heading text-xl font-bold text-brand-black mb-3">{s.title}</h3>
              <p className="text-sm text-brand-grey leading-relaxed max-w-xs mx-auto">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function ApiPage() {
  return (
    <div>
      <SEOHead title="Payment API Integration" description="Integrate CanDo Payments into your platform via our RESTful API. Sandbox access, developer tools, and engineer-to-engineer support." path="/partners/api" />
      <ApiHero />
      <WhatYouCanBuild />

      {/* What comes in the box */}
      <section className="py-20 bg-gray-50" data-testid="api-box">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="text-center mb-12">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">What Comes in the Box</motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnceSmall} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Terminal, title: "Sandbox environment", desc: "Full test environment with no time limits. Use Elavon-approved test cards to simulate every payment scenario — approvals, declines, 3-D Secure challenges, refunds, chargebacks." },
              { icon: FileText, title: "Clear documentation", desc: "REST API reference, SDKs, integration guides and sample code. Written by people who've actually built integrations, not just documented them." },
              { icon: Headphones, title: "Dedicated engineer", desc: "A real solution engineer assigned to your build. Not a tier-one support chat. Not a community forum. An engineer who knows your project." },
            ].map((item) => (
              <motion.div key={item.title} variants={fadeUp} className="bg-white border border-brand-light-grey rounded-2xl p-8 shadow-sm">
                <item.icon size={28} className="text-brand-aqua mb-4" />
                <h3 className="font-heading text-lg font-bold text-brand-black mb-2">{item.title}</h3>
                <p className="text-sm text-brand-grey leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Security */}
      <section className="py-20 bg-white" data-testid="api-security">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          <div className="text-center">
            <ShieldCheck size={32} className="text-brand-aqua mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-bold text-brand-black mb-4">Security and compliance, handled</h2>
            <p className="text-brand-grey leading-relaxed">
              Every integration built on our API inherits the full weight of Elavon's enterprise-grade security infrastructure: PCI DSS compliance, end-to-end encryption, tokenisation, 3-D Secure 2.x, and AI-powered real-time fraud scoring. Your clients' customers are protected by the same platform that handles payments for global airlines, hotel chains and retail giants. You don't have to build any of it — you just call the API.
            </p>
          </div>
        </div>
      </section>

      <ApiGetStarted />
      <PartnerBenefits />
      <TrustedProven />
      <PartnerCTA />
    </div>
  );
}
